<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <Menu/>

          <div class="element-box mt-4 d-flex px-3 py-4">
            <div class="col-lg-6 col-md-4 col-sm-12 px-0">
              <SelectVue
                  v-model="coachId"
                  :options="dropdownCoach"
                  classFormGroup="mb-0"
                  placeholder="Trainer auswählen"
                  :is-clear="true"
              />
            </div>
            <div class="col-auto text-center">
              <button class="btn btn-primary custom_search_button2" @click="getCoachData()">
                      <span v-if="isSearching">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {{ $t('general.button.search_in_progress') }}
                      </span>
                <span v-else>
                        {{ $t('general.button.search') }}
                      </span>
              </button>
            </div>
          </div>

          <h6 class="mt-5">{{ $t('dashboard.customer_information') }}</h6>
          <div class="row mt-3">
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="$t('general.active_customers')" :value="infoData?.totalActiveCoaching"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="$t('general.new_customers')" :value="infoData?.totalNewCoaching"/>
            </div>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6 class="mt-3">
              Einheiten im Jahr {{ filters?.year }} fertiggestellt
            </h6>

            <div class="row mt-4">
              <div class="col-lg-3 col-md-4 col-sm-12">
                <SelectVue
                    v-model="filterData.coachingType"
                    :options="dropdownCoachingType"
                    :is-clear="false"
                />
              </div>
              <div class="col-lg-2 col-md-4 col-sm-12">
                <SelectVue
                    v-model="filterData.year"
                    :options="dropdownYears"
                    :is-clear="false"
                />
              </div>

              <div class="col-auto text-center">
                <button class="btn btn-primary custom_search_button2" @click="getChartBarArea">
                  {{ $t('general.search') }}
                </button>
              </div>
            </div>
            <VueApexCharts width="100%" type="bar" :options="chartOptionsBar" :series="seriesSignBar"/>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6 class="mt-3">
              Arbeitsstunden und geleistete Einheiten im Jahr {{ filterData?.year }}
            </h6>
            <div class="row mt-4">
              <div class="col-sm-12">
                <VueApexCharts width="100%" type="area" :options="chartOptionsArea" :series="seriesWorksArea"/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box mt-4">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts width="100%" type="donut" :options="chartOptionsActiveCoach" :series="seriesActiveCoach"/>
        </div>
        <div class="element-box">
          <h6>{{ $t('general.new_customers') }}</h6>
          <VueApexCharts width="100%" type="donut" :options="chartOptionsNewCoach" :series="seriesNewCoach"/>
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardHeadPsychosocialServicesService from '@/api/Dashboard/HeadPsychosocialServices';
import CardInfo from '@/components/card/CardInfo.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import SelectVue from '@/components/SelectVue.vue';
import { ErrorToast } from '@/components/VueToast';
import { getCoachDropdown, getCoachingTypes, getYears } from '@/shared/dropdowns';
import { optionsChartArea, optionsChartBar, optionsChartDonut } from '@/shared/utils/apex';
import { MonthsUtils } from '@/shared/utils/MonthsUtils';
import { piniaUserStore } from '@/store/piniaUserStore';
import Menu from '@/views/App/Dashboard/HeadPsychosocialServices/Menu.vue';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import VueApexCharts from 'vue3-apexcharts';

const { t }                   = useI18n();
const chartOptionsActiveCoach = ref(optionsChartDonut);
const seriesActiveCoach       = ref([]);
const chartOptionsNewCoach    = ref(optionsChartDonut);
const seriesNewCoach          = ref([]);
const filterData              = ref({
  coachingType: 'socio_therapy_coaching',
  year: new Date().getFullYear(),
  coachId: '',
});

const coachId  = ref('');
const infoData = ref({});

const dropdownYears        = ref([]);
const dropdownCoachingType = ref([]);
const dropdownCoach        = ref([]);
const isSearching          = ref(false);

const seriesWorksArea  = ref([]);
const chartOptionsArea = ref({
  ...optionsChartArea,
  xaxis: {
    categories: MonthsUtils(t),
  },
  colors: [
    'rgba(67, 173, 78, 0.6)',
    'rgba(75, 192, 192, 0.6)',
  ],
});

const seriesSignBar   = ref([]);
const chartOptionsBar = ref({
  ...optionsChartBar,
  xaxis: {
    categories: MonthsUtils(t),
  },
  colors: [
    'rgba(67, 173, 78, 0.6)',
    'rgba(75, 192, 192, 0.6)',
  ],
  plotOptions: {
    bar: {
      distributed: false,
    },
  },
});

function getCoachData() {
  getChartBarArea();
  getInfoData();
}

async function getInfoData() {
  try {
    const { data } = await dashboardHeadPsychosocialServicesService.getCoachDashboard(coachId.value);
    infoData.value = data.data;

    seriesActiveCoach.value       = Object.values(data.data.countActiveCoaching);
    seriesNewCoach.value          = Object.values(data.data.countNewCoaching);
    chartOptionsActiveCoach.value = {
      ...chartOptionsActiveCoach.value,
      responsive: [ {
        breakpoint: 2480,
        options: {
          chart: {
            width: '100%',
            height: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      } ],
      labels: [
        `Soziotherapie ${data.data.countActiveCoaching?.socioTherapyCoaching}`,
        `Betreutes Wohnen ${data.data.countActiveCoaching?.assistedLivingCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptionsActiveCoach.value.plotOptions.pie.donut,
            labels: {
              ...chartOptionsActiveCoach.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptionsActiveCoach.value.plotOptions.pie.donut.labels.total,
                formatter: () => data.data.totalActiveCoaching.toString(),
              },
            },
          },
        },
      },
    };

    chartOptionsNewCoach.value = {
      ...chartOptionsNewCoach.value,
      responsive: [ {
        breakpoint: 2480,
        options: {
          chart: {
            width: '100%',
            height: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      } ],
      labels: [
        `Soziotherapie ${data.data.countNewCoaching?.socioTherapyCoaching}`,
        `Betreutes Wohnen ${data.data.countNewCoaching?.assistedLivingCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptionsNewCoach.value.plotOptions.pie.donut,
            labels: {
              ...chartOptionsNewCoach.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptionsNewCoach.value.plotOptions.pie.donut.labels.total,
                formatter: () => data.data.totalNewCoaching.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getChartBarArea() {
  try {
    isSearching.value     = true;
    const { data }        = await dashboardHeadPsychosocialServicesService.getCoachUnitsDoneChart(filterData.value, coachId.value);
    seriesSignBar.value   = [ data.data.chartData.signedUnits, data.data.chartData.unsignedUnits ];
    seriesWorksArea.value = [ data.data.chartData.totalUnits, data.data.chartData.sumHoursWorked ];
    isSearching.value     = false;
  } catch (error) {
    isSearching.value = false;
    await ErrorToast(error);
  }
}

onMounted(() => {
  getInfoData();
  getChartBarArea();
  getYears(dropdownYears);
  getCoachingTypes(dropdownCoachingType, [
    'group_coaching',
    'private_learning_coaching',
    'job_coaching',
    'occupational_therapy_coaching',
    'private_coaching',
    'case_management_coaching',
  ]);
  getCoachDropdown(dropdownCoach, { accessRights: 'therapist-coach-standortleiter', userType: 'intern' });
});

</script>

<style scoped>

::v-deep(.apexcharts-legend) {
  display: block !important;
}

</style>